/**
 * Organism: HelpPanel
 * Slide-over from right triggered by the ? icon in top nav.
 * Shows: search bar, context-aware article suggestions, category browse grid,
 * "Ask Sage" CTA, and "Contact Support" link.
 * PRD: help-center v1.0
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  X, Search, BookOpen, FolderOpen, DollarSign, Sparkles, Users,
  Shield, Wrench, Settings, AlertCircle, MessageCircle, ChevronRight,
  HelpCircle, Loader2, CheckCircle,
} from 'lucide-react'
import { tokens } from '@/design-system/tokens'
import { sanitizeHtml } from '@/lib/sanitize'
import { authedFetch } from '@/lib/api'
import { buildSupportContext } from '@/lib/supportContext'
import { useLocation } from 'react-router-dom'
import { Modal } from '@/components/molecules/Modal'

// ── Types ─────────────────────────────────────────────────────────────────

interface HelpCategory {
  id: number
  slug: string
  name: string
  icon: string
  position: number
}

interface HelpArticle {
  id: number
  category_id: number
  category_slug: string
  category_name: string
  title: string
  slug: string
  summary: string
  status: string
}

// ── Icon map ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  BookOpen:    <BookOpen size={18} />,
  FolderOpen:  <FolderOpen size={18} />,
  DollarSign:  <DollarSign size={18} />,
  Sparkles:    <Sparkles size={18} />,
  Users:       <Users size={18} />,
  Shield:      <Shield size={18} />,
  Wrench:      <Wrench size={18} />,
  Settings:    <Settings size={18} />,
  AlertCircle: <AlertCircle size={18} />,
}

// ── ContactSupportModal ───────────────────────────────────────────────────

interface ContactSupportModalProps {
  onClose: () => void
}

const SUPPORT_CATEGORIES = [
  'bug',
  'question',
  'idea',
]

const ContactSupportModal: React.FC<ContactSupportModalProps> = ({ onClose }) => {
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!category || !description.trim()) return
    setSubmitting(true)
    setError('')
    try {
      // Auto-attached diagnostics (#1311): route, app build, user agent.
      // buildSupportContext never throws — a failed version fetch just
      // degrades to an empty app_version.
      const context = await buildSupportContext()
      // credentials:'include' is kept on every call in this file — these help
      // endpoints authenticate by cookie. authedFetch only *adds* a bearer
      // when one exists, so cookie-only visitors behave exactly as before.
      const res = await authedFetch('/api/v1/help/support-tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category, description, context }),
      }, { redirectOnAuthFailure: false })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to submit ticket')
      }
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={440}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageCircle size={18} color={tokens.color.brand.gold} />
          Contact Support
        </span>
      }
    >
      {success ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <CheckCircle size={40} color='var(--st-success)' style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '15px', fontWeight: 600, color: tokens.color.text.primary, marginBottom: '8px' }}>
            Ticket submitted!
          </div>
          <div style={{ fontSize: '13px', color: tokens.color.text.muted, lineHeight: 1.5 }}>
            We'll get back to you within 4 hours on weekdays.
          </div>
          <button
            onClick={onClose}
            style={{
              marginTop: '20px',
              padding: '8px 20px',
              background: tokens.color.brand.gold,
              color: 'var(--bg-page)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: tokens.color.text.muted, marginBottom: '6px' }}>
              Issue category
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 10px',
                background: tokens.color.background.elevated,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: '6px',
                color: tokens.color.text.primary,
                fontSize: '13px',
                fontFamily: tokens.typography.fontFamily.sans,
              }}
            >
              <option value=''>Select a category…</option>
              {SUPPORT_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: tokens.color.text.muted, marginBottom: '6px' }}>
              What's happening?
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              required
              placeholder="Describe the issue and what you expected to happen…"
              rows={4}
              style={{
                width: '100%',
                padding: '8px 10px',
                background: tokens.color.background.elevated,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: '6px',
                color: tokens.color.text.primary,
                fontSize: '13px',
                fontFamily: tokens.typography.fontFamily.sans,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {error && (
            <div style={{ fontSize: '12px', color: 'var(--st-error)', marginBottom: '12px' }}>{error}</div>
          )}
          <button
            type='submit'
            disabled={submitting || !category || !description.trim()}
            style={{
              width: '100%',
              padding: '10px',
              background: tokens.color.brand.gold,
              color: 'var(--bg-page)',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              opacity: submitting || !category || !description.trim() ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            {submitting && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {submitting ? 'Submitting…' : 'Submit ticket'}
          </button>
        </form>
      )}
    </Modal>
  )
}

// ── HelpPanel ─────────────────────────────────────────────────────────────

interface HelpPanelProps {
  open: boolean
  onClose: () => void
  onOpenSage?: () => void
}

export const HelpPanel: React.FC<HelpPanelProps> = ({ open, onClose, onOpenSage }) => {
  const location = useLocation()
  const panelRef = useRef<HTMLDivElement>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [categories, setCategories] = useState<HelpCategory[]>([])
  const [contextArticles, setContextArticles] = useState<HelpArticle[]>([])
  const [searchResults, setSearchResults] = useState<HelpArticle[]>([])
  const [loadingContext, setLoadingContext] = useState(false)
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [showContactSupport, setShowContactSupport] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState<(HelpArticle & { content?: string }) | null>(null)
  const [loadingArticle, setLoadingArticle] = useState(false)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onClose])

  // Fetch categories once
  useEffect(() => {
    if (!open || categories.length > 0) return
    authedFetch('/api/v1/help/categories', { credentials: 'include' }, { redirectOnAuthFailure: false })
      .then(r => r.json())
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {})
  }, [open, categories.length])

  // Fetch context-aware articles when panel opens or route changes
  useEffect(() => {
    if (!open) return
    setLoadingContext(true)
    authedFetch(`/api/v1/help/articles?route=${encodeURIComponent(location.pathname)}`, { credentials: 'include' }, { redirectOnAuthFailure: false })
      .then(r => r.json())
      .then(data => setContextArticles(Array.isArray(data) ? data.slice(0, 3) : []))
      .catch(() => {})
      .finally(() => setLoadingContext(false))
  }, [open, location.pathname])

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    const timer = setTimeout(() => {
      setLoadingSearch(true)
      authedFetch(`/api/v1/help/articles?q=${encodeURIComponent(searchQuery)}`, { credentials: 'include' }, { redirectOnAuthFailure: false })
        .then(r => r.json())
        .then(data => setSearchResults(Array.isArray(data) ? data : []))
        .catch(() => {})
        .finally(() => setLoadingSearch(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const openArticle = useCallback(async (slug: string) => {
    setLoadingArticle(true)
    try {
      const res = await authedFetch(`/api/v1/help/articles/${slug}`, { credentials: 'include' }, { redirectOnAuthFailure: false })
      const data = await res.json()
      setSelectedArticle(data)
    } catch {
      // ignore
    } finally {
      setLoadingArticle(false)
    }
  }, [])

  if (!open) return null

  // Panel styles
  const panel: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '380px',
    maxWidth: '100vw',
    background: tokens.color.background.surface,
    borderLeft: `1px solid ${tokens.color.border.default}`,
    boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: tokens.typography.fontFamily.sans,
    overflowY: 'auto',
  }

  const header: React.CSSProperties = {
    padding: '16px',
    borderBottom: `1px solid ${tokens.color.border.default}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  }

  const section: React.CSSProperties = {
    padding: '16px',
    borderBottom: `1px solid ${tokens.color.border.subtle ?? tokens.color.border.default}`,
  }

  const sectionTitle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.color.text.muted,
    marginBottom: '10px',
  }

  const articleRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 0',
    cursor: 'pointer',
    borderBottom: `1px solid ${tokens.color.border.default}`,
  }

  return (
    <>
      <div ref={panelRef} style={panel}>
        {/* Header */}
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedArticle ? (
              <button
                onClick={() => setSelectedArticle(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.color.text.muted, padding: '2px', display: 'flex' }}
              >
                <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              </button>
            ) : (
              <HelpCircle size={16} color={tokens.color.brand.gold} />
            )}
            <span style={{ fontSize: '14px', fontWeight: 600, color: tokens.color.text.primary }}>
              {selectedArticle ? selectedArticle.title : 'Help Centre'}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.color.text.muted, padding: '2px', display: 'flex' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Article view */}
        {selectedArticle && (
          <div style={{ flex: 1, padding: '20px 16px', overflowY: 'auto' }}>
            {loadingArticle ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Loader2 size={20} color={tokens.color.text.muted} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <>
                <div style={{ fontSize: '11px', color: tokens.color.text.muted, marginBottom: '8px' }}>
                  {selectedArticle.category_name}
                </div>
                <div style={{ fontSize: '13px', color: tokens.color.text.secondary, marginBottom: '16px', lineHeight: 1.5 }}>
                  {selectedArticle.summary}
                </div>
                <div
                  style={{ fontSize: '13px', color: tokens.color.text.primary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(selectedArticle.content || '')) }}
                />
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${tokens.color.border.default}` }}>
                  <button
                    onClick={() => setShowContactSupport(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: tokens.color.text.muted,
                      fontSize: '12px',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    Still stuck? Contact Support
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Main panel view */}
        {!selectedArticle && (
          <>
            {/* Search */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${tokens.color.border.default}` }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: tokens.color.text.muted }} />
                <input
                  type='text'
                  placeholder='Search help articles…'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '8px 10px 8px 32px',
                    background: tokens.color.background.elevated,
                    border: `1px solid ${tokens.color.border.default}`,
                    borderRadius: '6px',
                    color: tokens.color.text.primary,
                    fontSize: '13px',
                    fontFamily: tokens.typography.fontFamily.sans,
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                />
                {loadingSearch && (
                  <Loader2 size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: tokens.color.text.muted, animation: 'spin 1s linear infinite' }} />
                )}
              </div>
            </div>

            {/* Search results */}
            {searchQuery.trim() && (
              <div style={section}>
                <div style={sectionTitle}>Search results</div>
                {loadingSearch && searchResults.length === 0 && (
                  <div style={{ color: tokens.color.text.muted, fontSize: '12px' }}>Searching…</div>
                )}
                {!loadingSearch && searchResults.length === 0 && (
                  <div style={{ color: tokens.color.text.muted, fontSize: '12px' }}>No articles found for "{searchQuery}"</div>
                )}
                {searchResults.map(article => (
                  <div key={article.id} style={articleRow} onClick={() => openArticle(article.slug)}>
                    <HelpCircle size={14} color={tokens.color.text.muted} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: tokens.color.text.primary, marginBottom: '2px' }}>
                        {article.title}
                      </div>
                      <div style={{ fontSize: '11px', color: tokens.color.text.muted, lineHeight: 1.4 }}>
                        {article.summary}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Context-aware suggestions */}
            {!searchQuery.trim() && (
              <div style={section}>
                <div style={sectionTitle}>Suggested for this page</div>
                {loadingContext && <div style={{ color: tokens.color.text.muted, fontSize: '12px' }}>Loading…</div>}
                {contextArticles.map(article => (
                  <div key={article.id} style={articleRow} onClick={() => openArticle(article.slug)}>
                    <HelpCircle size={14} color={tokens.color.text.muted} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: tokens.color.text.primary, marginBottom: '2px' }}>
                        {article.title}
                      </div>
                      <div style={{ fontSize: '11px', color: tokens.color.text.muted, lineHeight: 1.4 }}>
                        {article.summary}
                      </div>
                    </div>
                  </div>
                ))}
                {!loadingContext && contextArticles.length === 0 && (
                  <div style={{ color: tokens.color.text.muted, fontSize: '12px' }}>Browse categories below to find help.</div>
                )}
              </div>
            )}

            {/* Ask Sage CTA */}
            {!searchQuery.trim() && onOpenSage && (
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${tokens.color.border.default}` }}>
                <button
                  onClick={() => { onClose(); onOpenSage() }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(239, 248, 144, 0.08)',
                    border: `1px solid rgba(239, 248, 144, 0.2)`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    fontFamily: tokens.typography.fontFamily.sans,
                  }}
                >
                  <Sparkles size={16} color="var(--brand-gold)" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--brand-gold)' }}>Ask Sage</div>
                    <div style={{ fontSize: '11px', color: tokens.color.text.muted }}>Get a direct answer from your AI assistant</div>
                  </div>
                </button>
              </div>
            )}

            {/* Category browse grid */}
            {!searchQuery.trim() && (
              <div style={section}>
                <div style={sectionTitle}>Browse by category</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setSearchQuery('')
                        // Trigger search by category — fetch and show results
                        setLoadingSearch(true)
                        authedFetch(`/api/v1/help/articles?category=${cat.slug}`, { credentials: 'include' }, { redirectOnAuthFailure: false })
                          .then(r => r.json())
                          .then(data => { setSearchResults(Array.isArray(data) ? data : []); setSearchQuery(cat.name) })
                          .catch(() => {})
                          .finally(() => setLoadingSearch(false))
                      }}
                      style={{
                        padding: '10px 8px',
                        background: tokens.color.background.elevated,
                        border: `1px solid ${tokens.color.border.default}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        fontFamily: tokens.typography.fontFamily.sans,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = tokens.color.background.page ?? tokens.color.background.elevated)}
                      onMouseLeave={e => (e.currentTarget.style.background = tokens.color.background.elevated)}
                    >
                      <span style={{ color: tokens.color.text.muted }}>
                        {ICON_MAP[cat.icon] ?? <HelpCircle size={18} />}
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 500, color: tokens.color.text.secondary, textAlign: 'center', lineHeight: 1.3 }}>
                        {cat.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Support link */}
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <button
                onClick={() => setShowContactSupport(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: tokens.color.text.muted,
                  fontSize: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: 0,
                }}
              >
                <MessageCircle size={13} />
                Contact Support
              </button>
            </div>
          </>
        )}
      </div>

      {/* Contact Support Modal */}
      {showContactSupport && (
        <ContactSupportModal onClose={() => setShowContactSupport(false)} />
      )}

      {/* Spin keyframe (injected once) */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  )
}

// ── Simple markdown → html converter (no dependency needed) ──────────────

function markdownToHtml(md: string): string {
  return md
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Headings
    .replace(/^### (.*?)$/gm, '<h3 style="font-size:13px;font-weight:600;margin:14px 0 6px">$1</h3>')
    .replace(/^## (.*?)$/gm, '<h2 style="font-size:14px;font-weight:700;margin:16px 0 8px">$1</h2>')
    .replace(/^# (.*?)$/gm, '<h1 style="font-size:16px;font-weight:700;margin:16px 0 8px">$1</h1>')
    // Table rows (basic)
    .replace(/^\|(.+)\|$/gm, (_, row) => {
      if (row.includes('---')) return ''
      const cells = row.split('|').map((c: string) => c.trim())
      return '<tr>' + cells.map((c: string) => `<td style="padding:4px 8px;border:1px solid #333">${c}</td>`).join('') + '</tr>'
    })
    // Lists
    .replace(/^- (.*?)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.*?)$/gm, '<li>$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p style="margin:8px 0">')
    .replace(/\n/g, '<br/>')
    // Wrap
    .replace(/(<li>.*?<\/li>)/gs, '<ul style="margin:8px 0;padding-left:20px">$1</ul>')
}
